import type { Theme } from "@mui/material";
import { CircularProgress, Stack, Typography, useMediaQuery } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import FileIcon from "@entrilia/shared/components/FileIcon";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { formatFileSize, getFileExtension, isFileDownloadAllowed } from "@entrilia/shared/utilities/fileHelper";
import type { FundraisingFile } from "../../api/types/fundraisingTypes";
import FundraisingDocumentActionsContext from "./FundraisingDocumentActionsContext";

const FileNameCell = ({ row }: { row: FundraisingFile }) => {
  const { downloadingFileId, showFilePreview } = useDefinedContext(FundraisingDocumentActionsContext);

  if (!row.name) {
    return null;
  }

  const preventDownload = !isFileDownloadAllowed(row.accessOptions);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", pl: 3 }}>
      {downloadingFileId === row.fileId ? (
        <CircularProgress size={20} color="secondary" sx={{ flexShrink: 0 }} />
      ) : (
        <FileIcon fileExtension={getFileExtension(row.name)} />
      )}
      <Typography
        noWrap
        sx={(t) => ({ cursor: "pointer", "&:hover": { color: t.palette.primary.main } })}
        onClick={() => showFilePreview(row.fileId, preventDownload)}
      >
        {row.name}
      </Typography>
    </Stack>
  );
};

const getGridColumnVisibilityModel = ({ showSize }: { showSize: boolean }) => ({
  name: false,
  section: false,
  size: showSize,
});

const gridRowGroupingModel = ["section"];

const groupingColumnDefinition: GridGroupingColDefOverride<FundraisingFile> = {
  leafField: "name",
  hideDescendantCount: true,
  flex: 1,
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
};

const columnDefinitions: GridColDef<FundraisingFile>[] = [
  {
    field: "section",
    renderCell: ({ value }) => <Typography variant="subtitle2">{value}</Typography>,
  },
  {
    field: "name",
    headerName: "Name",
    renderCell: ({ row }) => <FileNameCell row={row} />,
  },
  {
    field: "size",
    headerName: "Size",
    type: "number",
    width: 80,
    valueFormatter: (value: number) => (value ? formatFileSize(value) : null),
  },
  {
    field: "publishedAt",
    headerName: "Date",
    width: 100,
    valueFormatter: (value: string) => (value ? convertISODate(value) : ""),
  },
];

interface Props {
  files: FundraisingFile[];
}

const getRowId = (row: FundraisingFile) => row.fileId;

const FundraisingDocumentsGrid = ({ files }: Props) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const apiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const columnVisibilityModel = useMemo(
    () => getGridColumnVisibilityModel({ showSize: !isSmallScreen }),
    [isSmallScreen]
  );

  return (
    <DataGrid<FundraisingFile>
      apiRef={apiRef}
      getRowId={getRowId}
      columns={columnDefinitions}
      groupingColDef={groupingColumnDefinition}
      rows={files}
      columnHeaderHeight={isSmallScreen ? 0 : 36}
      disableColumnResize
      disableColumnPinning
      disableColumnSorting
      disableColumnReorder
      disableMultipleRowSelection
      isGroupExpandedByDefault={isGroupExpanded}
      rowGroupingModel={gridRowGroupingModel}
      columnVisibilityModel={columnVisibilityModel}
      noScrollableContainer
    />
  );
};

export default FundraisingDocumentsGrid;
